import { useEffect, useState } from 'react'
import { useAuthStore } from '../../store/authStore'
import { useGameStore } from '../../store/gameStore'
import type { Zone, BattleHighlight } from '@territory-nexus/shared'
import { GAME_CONFIG, UNIT_ABILITIES, getZoneModifiers } from '@territory-nexus/shared'
import { api } from '../../services/api'
import {
  Loader2,
  Swords,
  Shield,
  Heart,
  Zap,
  XCircle,
  Sparkles,
} from 'lucide-react'
import clsx from 'clsx'
import { UnitSprite } from './sprites'
import BattleResultOverlay from './BattleResultOverlay'

interface BattlePanelProps {
  zoneId?: string
  onClose?: () => void
}

interface BattleResultUnit {
  id: string
  unitType: string
  currentHp: number
  maxHp: number
}

interface BattleResultStructure {
  id: string
  structureType: string
  level: number
  currentHp: number
  maxHp: number
}

interface BattleResult {
  battleId: string
  victory: boolean
  turns: number
  highlights: BattleHighlight[]
  attackerUnits: BattleResultUnit[]
  defenderUnits: BattleResultUnit[]
  structures?: BattleResultStructure[]
  modDrop?: { rarity: string; modType: string }
}

export default function BattlePanel({ zoneId, onClose }: BattlePanelProps) {
  const { units, fetchUnits, fetchResources } = useGameStore()

  const [battleResult, setBattleResult] = useState<BattleResult | null>(null)
  const [selectedUnits, setSelectedUnits] = useState<string[]>([])
  const [isInitiating, setIsInitiating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [targetZone, setTargetZone] = useState<Zone | null>(null)

  const idleUnits = units.filter((u) => u.status === 'idle')
  const isSanctuary = targetZone?.hexId ? getZoneModifiers(targetZone.hexId).some(m => m.id === 'sanctuary') : false

  // Load target zone info
  useEffect(() => {
    if (zoneId) {
      api.get<{ success: boolean; data: Zone }>(`/zones/${zoneId}`)
        .then((res) => {
          if (res.data.success) setTargetZone(res.data.data)
        })
        .catch(console.error)
    }
  }, [zoneId])

  useEffect(() => {
    fetchUnits()
  }, [fetchUnits])

  const toggleUnitSelection = (unitId: string) => {
    setSelectedUnits((prev) => {
      if (prev.includes(unitId)) {
        return prev.filter((id) => id !== unitId)
      }
      if (prev.length >= GAME_CONFIG.MAX_UNITS_IN_BATTLE) return prev
      return [...prev, unitId]
    })
  }

  const initiateBattle = async () => {
    if (!zoneId || selectedUnits.length < GAME_CONFIG.MIN_UNITS_IN_BATTLE) return

    setIsInitiating(true)
    setError(null)

    try {
      const response = await api.post<{
        success: boolean
        data: BattleResult
      }>('/battles/initiate', { zoneId, unitIds: selectedUnits })

      if (response.data.success) {
        setBattleResult(response.data.data)
        // Refresh units and resources after battle
        fetchUnits()
        fetchResources()
        useAuthStore.getState().refreshPlayer()
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to initiate battle')
    } finally {
      setIsInitiating(false)
    }
  }

  // ─── Battle Result View ──────────────────────────────────────
  if (battleResult) {
    return (
      <BattleResultOverlay
        victory={battleResult.victory}
        turns={battleResult.turns}
        battleType="pvp"
        playerUnits={battleResult.attackerUnits}
        enemyUnits={battleResult.defenderUnits}
        structures={battleResult.structures}
        highlights={battleResult.highlights}
        modDrop={battleResult.modDrop}
        onContinue={() => {
          fetchUnits()
          fetchResources()
          onClose?.()
        }}
      />
    )
  }

  // ─── Unit Selection (Pre-Battle) ────────────────────────────
  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-display font-bold text-gradient-solar">Initiate Battle</h2>
            {targetZone && (
              <p className="text-slate-400 text-xs mt-0.5">
                Target: Zone {targetZone.hexId.substring(0, 12)}...
                {targetZone.ownerId ? ' (Defended)' : ' (Undefended)'}
              </p>
            )}
          </div>
          {onClose && (
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <XCircle className="w-6 h-6" />
            </button>
          )}
        </div>

        {isSanctuary && (
          <div className="glass border border-amber-500/30 bg-amber-500/10 rounded-lg p-3 mb-4">
            <p className="text-amber-300 text-sm font-medium">🕊 This zone is a Sanctuary — PvP attacks are blocked</p>
          </div>
        )}

        {error && (
          <div className="glass border border-red-500/30 bg-red-500/10 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="text-xs text-red-400/60 hover:text-red-400 mt-1">
              Dismiss
            </button>
          </div>
        )}

        {/* Unit Selection */}
        <div className="glass rounded-xl p-4 mb-4 border border-cyan-500/20">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">
              Select {GAME_CONFIG.MIN_UNITS_IN_BATTLE}-{GAME_CONFIG.MAX_UNITS_IN_BATTLE} Units
            </h3>
            <span className="text-cyan-400 font-medium text-sm">{selectedUnits.length}/{GAME_CONFIG.MAX_UNITS_IN_BATTLE}</span>
          </div>

          {idleUnits.length === 0 ? (
            <div className="text-center py-6">
              <Shield className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No available units</p>
              <p className="text-slate-500 text-xs">Create or recall units to start a battle</p>
            </div>
          ) : (
            <div className="space-y-2">
              {idleUnits.map((unit) => {
                const isSelected = selectedUnits.includes(unit.id)
                const ability = UNIT_ABILITIES[unit.unitType]
                return (
                  <div
                    key={unit.id}
                    onClick={() => toggleUnitSelection(unit.id)}
                    className={clsx(
                      'glass rounded-lg p-3 cursor-pointer transition border',
                      isSelected
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-white/10 hover:border-white/20'
                    )}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <UnitSprite unitType={unit.unitType} level={unit.level} status="idle" size="sm" showLevel={false} animate={false} />
                        <div>
                          <span className="font-medium text-white capitalize text-sm">{unit.unitType.replace('_', ' ')}</span>
                          <span className="text-[10px] text-slate-500 ml-1.5">Lv.{unit.level}</span>
                        </div>
                      </div>
                      {isSelected && (
                        <span className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center text-[10px] font-bold text-white">
                          {selectedUnits.indexOf(unit.id) + 1}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3 text-red-400" />
                        {unit.stats.hp}
                      </span>
                      <span className="flex items-center gap-1">
                        <Swords className="w-3 h-3 text-orange-400" />
                        {unit.stats.atk}
                      </span>
                      <span className="flex items-center gap-1">
                        <Shield className="w-3 h-3 text-blue-400" />
                        {unit.stats.def}
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-400" />
                        {unit.stats.spd}
                      </span>
                    </div>
                    {/* Passive ability */}
                    {ability && (
                      <p className="text-[10px] text-purple-400/80 mt-1.5">
                        <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />
                        {ability.name}: {ability.description}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Battle Button */}
        <button
          onClick={initiateBattle}
          disabled={isSanctuary || selectedUnits.length < GAME_CONFIG.MIN_UNITS_IN_BATTLE || isInitiating}
          className={clsx(
            'w-full py-3 rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2',
            !isSanctuary && selectedUnits.length >= GAME_CONFIG.MIN_UNITS_IN_BATTLE
              ? 'bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white'
              : 'bg-slate-800 text-slate-500 cursor-not-allowed'
          )}
        >
          {isInitiating ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Battling...
            </>
          ) : (
            <>
              <Swords className="w-5 h-5" />
              Battle!
            </>
          )}
        </button>
      </div>
    </div>
  )
}
